import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, Skeleton } from '@mui/material';
import homebg from '../../Assets/homebg.png';
import BottomNavigation from '../BottomNavigation/BottomNavigation';
import { useNavigate, useParams } from 'react-router-dom';
import config from '../../Config';

export default function RoomListScreen() {
  const navigate = useNavigate();
  const { reservationId } = useParams();

  const [booking, setBooking] = useState(null);
  const [propertyData, setPropertyData] = useState(null);
  const [frontDoorCode, setFrontDoorCode] = useState(null);
  const [frontDoorError, setFrontDoorError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingFrontDoor, setLoadingFrontDoor] = useState(false);

  useEffect(() => {
    if (!reservationId) {
      setError('No reservation ID found in URL');
      setLoading(false);
      return;
    }

    const fetchBooking = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${config.apiBaseUrl}/guest/user-Hotel-reservation/${reservationId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) throw new Error('Failed to fetch booking data');

        const data = await response.json();
        setBooking(data.booking);
        setPropertyData(data.propertyData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [reservationId]);

  const handleUnlockFrontDoor = async () => {
    if (!propertyData?.frontLock?.lockId) return;

    setLoadingFrontDoor(true);
    setFrontDoorError(null);
    setFrontDoorCode(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${config.apiBaseUrl}/guest/get-front-door-pass-key/${reservationId}/${propertyData.frontLock.lockId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok || data.success === false) {
        setFrontDoorError(data.message || 'Failed to get front door code');
        return;
      }

      const passcode = data?.data?.password;

      if (!passcode) {
        setFrontDoorError('No front door password found.');
        return;
      }

      setFrontDoorCode(passcode);
    } catch (err) {
      setFrontDoorError('Something went wrong. Please try again.');
    } finally {
      setLoadingFrontDoor(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          maxWidth: '400px',
          mx: 'auto',
          px: 3,
          pt: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Skeleton variant="text" width={180} height={32} sx={{ mx: 'auto' }} />
        <Skeleton
          variant="rectangular"
          height={200}
          sx={{
            borderRadius: '20px',
            backgroundColor: '#e0e0e0',
          }}
        />
        <Skeleton variant="text" width={220} height={28} sx={{ mx: 'auto' }} />
        <Skeleton variant="text" width={140} height={24} sx={{ mx: 'auto', mt: 2 }} />
        {[1, 2, 3].map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            height={56}
            sx={{
              borderRadius: '28px',
              backgroundColor: '#d0d0d0',
            }}
          />
        ))}
        <Box sx={{ height: 56 }} />
      </Box>
    );
  }

  if (error) {
    return <Typography sx={{ textAlign: 'center', mt: 4, color: 'red' }}>{error}</Typography>;
  }

  const guestName = booking?.guestList
    ? Object.values(booking.guestList)
        .map((guest) => `${guest.guestFirstName} ${guest.guestLastName}`)
        .join(', ')
    : booking?.guestName || 'Guest';

  const rooms = booking?.assigned?.length > 0 ? booking.assigned : (booking?.unassigned ?? []);

  const isCheckInDayAndActive = (() => {
    if (!booking?.startDate || !booking?.status) return false;
    const today = new Date();
    const checkInDate = new Date(booking.startDate);
    const sameDay =
      today.getFullYear() === checkInDate.getFullYear() &&
      today.getMonth() === checkInDate.getMonth() &&
      today.getDate() === checkInDate.getDate();
    const notCheckedOut = booking.status.toLowerCase() !== 'check_out';
    return sameDay && notCheckedOut;
  })();

  const handleRoomClick = (room) => {
    if (!isCheckInDayAndActive) return;
    navigate('/unlock-room', {
      state: {
        roomNumber: room.roomTypeNameShort || room.roomName || room.roomTypeName || 'Unknown',
        roomID: room.roomID,
        guestName,
        hasDigiLock: propertyData?.hasDigiLock || false,
        reservationId: reservationId,
      },
    });
  };

  const hotelName = propertyData?.propertyName || 'Hotel';
  const showFrontLockButton = propertyData?.isFrontLock;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '400px',
        margin: '0 auto',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
          mt: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: '#000',
            fontSize: '20px',
          }}
        >
          {guestName}
        </Typography>
      </Box>

      <Container maxWidth="sm" sx={{ flex: 1, px: 3, pb: 12 }}>
        <Box
          sx={{
            borderRadius: '20px',
            mb: 3,
            overflow: 'hidden',
            backgroundImage: propertyData?.propertyImage?.[0]?.image
              ? `url(${propertyData.propertyImage[0].image})`
              : `url(${homebg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            height: '200px',
          }}
        />

        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontSize: '24px',
              color: '#000',
              mb: 1,
            }}
          >
            {hotelName}
          </Typography>

          {showFrontLockButton && (
            <>
              {frontDoorCode ? (
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '18px',
                    color: '#2e7d7b',
                    mt: 1,
                  }}
                >
                  🔐 Front Door Code: <strong style={{ fontSize: '20px' }}>{frontDoorCode}#</strong>
                </Typography>
              ) : (
                <>
                  <Button
                    variant="contained"
                    onClick={handleUnlockFrontDoor}
                    disabled={loadingFrontDoor}
                    startIcon={
                      <span role="img" aria-label="lock">
                        🔓
                      </span>
                    }
                    sx={{
                      mt: 1,
                      backgroundColor: '#2e7d7b',
                      color: 'white',
                      borderRadius: '20px',
                      textTransform: 'none',
                      '&:hover': { backgroundColor: '#1e5a58' },
                    }}
                  >
                    {loadingFrontDoor ? 'Fetching Code...' : 'Unlock Front Door'}
                  </Button>

                  {frontDoorError && (
                    <Typography
                      sx={{
                        mt: 2,
                        color: 'red',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      {frontDoorError}
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </Box>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: '18px',
            color: '#000',
            mb: 4,
          }}
        >
          Room Details
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {rooms.length === 0 ? (
            <Typography>No rooms assigned.</Typography>
          ) : (
            rooms.map((room) => (
              <Button
                key={room.roomID}
                fullWidth
                variant="contained"
                onClick={() => handleRoomClick(room)}
                disabled={!isCheckInDayAndActive}
                sx={{
                  backgroundColor: isCheckInDayAndActive ? '#2e7d7b' : '#b0b0b0',
                  color: 'white',
                  py: 2.2,
                  borderRadius: '28px',
                  fontSize: '16px',
                  fontWeight: 600,
                  textTransform: 'none',
                  height: '56px',
                  boxShadow: 'none',
                  cursor: isCheckInDayAndActive ? 'pointer' : 'not-allowed',
                  '&:hover': {
                    backgroundColor: isCheckInDayAndActive ? '#1e5a58' : '#b0b0b0',
                    boxShadow: 'none',
                  },
                }}
              >
                Room {room.roomTypeNameShort || room.roomName || room.roomTypeName || 'Unknown'}
              </Button>
            ))
          )}
        </Box>
      </Container>

      <BottomNavigation />
    </Box>
  );
}
